import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface StickyMobileCtaProps {
  label: string;
  microcopy?: string;
  to?: string;
  /** px to scroll before showing. Default 600 (past most hero sections) */
  threshold?: number;
}

/**
 * Bottom-anchored CTA that appears on mobile only after the user scrolls
 * past the hero. Highest-leverage conversion lift for social traffic.
 */
const StickyMobileCta = ({
  label,
  microcopy,
  to = "/auth?tab=signup",
  threshold = 600,
}: StickyMobileCtaProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return (
    <div
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pt-3 transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
      }`}
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
    >
      <div className="glass rounded-2xl border border-border/60 shadow-lg shadow-black/10 p-3">
        <Button variant="hero" size="lg" className="w-full" asChild>
          <Link to={to}>
            {label} <ArrowRight className="ml-1" size={18} />
          </Link>
        </Button>
        {microcopy && (
          <p className="text-[11px] text-center text-muted-foreground mt-2">{microcopy}</p>
        )}
      </div>
    </div>
  );
};

export default StickyMobileCta;